/** The console's Build tab (live, for a commander): what to build, placed
 *  with the cursor; the player's engineers go to it unbidden. Below, what's
 *  under construction and how far along. */

import { For, Show, createSignal, onCleanup, type JSX } from "solid-js";
import { engineers, siteRef } from "../state/engineers";
import { recipes, setRecipes, type Recipe } from "../state/recipes";
import { v31 } from "../state/features";
import { RecipePicker, RecipeIcon } from "./RecipePicker";

/** A structure to place, as the server's build command names it. */
export interface Placing {
  what: "depot" | "station" | "barracks" | "mine" | "plant" | "track" | "breaker" | "guard_post" | "foundry" | "trade_post" | "training_camp" | "comms_post";
  resource?: string;
  recipe?: string;
}

/** A construction site, from the commander's state. */
export interface Site {
  id: number;
  what: string;
  at: [number, number];
  need: number;
  done: number;
  resource?: string;
  recipe?: string;
  /** Track: its far end. */
  to?: [number, number];
  /** Why it isn't being built, if it isn't (the server's word). */
  blocked?: "no_engineers" | "no_steel" | "orders" | "reach" | "queued";
  /** Ordered past the open sites' limit: waits its turn (opens oldest
   *  first as others finish). */
  queued?: boolean;
}

/** A blocked site's reason: short (on the map) and in full, with what to do. */
export const BLOCKED: Record<string, [string, string]> = {
  no_engineers: ["No engineers", "You have no engineers left to build it."],
  no_steel: ["No steel", "You're out of steel to pay for it: steel comes from a steelworks (iron ore and coal)."],
  orders: ["Engineers busy", "Your engineers are under orders elsewhere. Stop them, or send them here."],
  reach: ["Out of reach", "None of your engineers can get there."],
  queued: ["Waiting", "Your engineers are building another site first."],
};

export const siteName = (s: Site) =>
  // A mine digs every ore in its reach: until built (and named by what it
  // found), it's a mine.
  s.what === "mine" ? "Mine" : s.what === "plant" ? (s.recipe ?? "Plant") : s.what[0].toUpperCase() + s.what.slice(1);

/** A plant's tip from its recipe, where it has none of its own: what it
 *  takes and makes, both by rail to and from its station. */
function recipeTip(name: string): string | undefined {
  const r = recipes().find((x) => x.name === name);
  if (!r || !r.outputs.length) return undefined;
  const list = (l: [number, string, number][]) => l.map(([, g]) => g.toLowerCase()).join(" and ");
  return r.inputs.length
    ? `${name}: makes ${list(r.outputs)} from ${list(r.inputs)}, brought to its station by rail.`
    : `${name}: makes ${list(r.outputs)}.`;
}

/** Cancelling a construction site (the game sets it): all its steel back. */
let onCancel: (at: [number, number][]) => void = () => {};
export function setCancelSite(f: (at: [number, number][]) => void): void {
  onCancel = f;
}
export const cancelSites = (at: [number, number][]) => onCancel(at);

const [sites, setSites] = createSignal<Site[]>([]);
/** How many sites may be open at once (the rest queue). */
const [siteLimit, setSiteLimit] = createSignal(16);
export { setSiteLimit };
/** A tick a second while the Build tab's open (bars fill between reports). */
const [now, setNow] = createSignal(0);
const [placing, setPlacing] = createSignal<Placing | null>(null);
export { sites, setSites, setPlacing };
/** Every recipe's name (for a plant's card, and the Plant button's choice). */
export const recipeNames = () => recipes().map((r) => r.name);
/** What the Plant button builds (remembered). */
const [plantChoice, setPlantChoice] = createSignal<string>(
  (() => {
    try {
      return localStorage.getItem("ferro.plantChoice") ?? "Steelworks";
    } catch {
      return "Steelworks";
    }
  })(),
);

/** Each site's progress when it last moved, and how fast it went to get
 *  there, so its bar fills smoothly between the server's reports (every
 *  5 s; the work itself is done in steps every 30 s, so most reports
 *  bring no change). */
const pace = new Map<number, { done: number; at: number; rate: number }>();

/** Notes the sites' progress from a new report (see `siteDone`). */
export function noteSites(list: Site[]): void {
  const now = performance.now();
  const next = new Map<number, { done: number; at: number; rate: number }>();
  for (const s of list) {
    const was = pace.get(s.id);
    if (s.blocked) next.set(s.id, { done: s.done, at: now, rate: 0 });
    // No change since: still going at the pace it went (a minute without
    // any, and it's stopped).
    else if (was && s.done === was.done) next.set(s.id, now - was.at > 60_000 ? { ...was, rate: 0 } : was);
    else next.set(s.id, { done: s.done, at: now, rate: was && s.done > was.done ? (s.done - was.done) / (now - was.at) : 0 });
  }
  pace.clear();
  for (const [k, v] of next) pace.set(k, v);
}

/** A site's work done now: the last report, plus what it's done since at
 *  the pace it went before (never past what it needs). */
export function siteDone(s: Site): number {
  const p = pace.get(s.id);
  if (!p) return s.done;
  return Math.min(s.need, p.done + p.rate * (performance.now() - p.at));
}

const name = siteName;

/** Each structure's steel (construction::WORK_PER_STEEL of its work); the
 *  steel chain (mines, the steelworks, track) is built even without. */
export const steelOf = (p: Placing) =>
  p.what === "track" ? "5/hex" : (({ depot: 100, station: 150, barracks: 150, mine: 200, plant: 300, breaker: 2000, guard_post: 300, foundry: 10000, trade_post: 1000, training_camp: 1500, comms_post: 500 } as Record<string, number>)[p.what] ?? 0).toLocaleString("en-US");

/** Structures research unlocks: the technology, and what to say until
 *  it's known. The storm breaker is only in worlds with a storm wall. */
const LOCKS: Record<string, [tech: number, why: string]> = {
  breaker: [19, "Research Storm breaching first (Engineering, after Heavy fabrication)"],
  guard_post: [20, "Research Automated defences first (Military)"],
  foundry: [4, "Research Titans first (Engineering)"],
};
const [stormWorld, setStormWorld] = createSignal(false);
const [knownTechs, setKnownTechs] = createSignal<number[]>([]);
export { stormWorld, setStormWorld };

/** Why a structure can't be built yet, if it can't (research). */
export function lockedWhy(p: Placing): string | null {
  const lock = LOCKS[p.what];
  return lock && !knownTechs().includes(lock[0]) ? lock[1] : null;
}

/** What a structure does, where it isn't plain from its name. */
export const STRUCTURE_TIPS: Record<string, string> = {
  guard_post: "Automated defence: holds this hex and fires on anyone who enters your ground or shoots at it. Never moves.",
  Mint: "Mint: strikes copper into credits at its worth. Credits trade without track, and across the storm wall.",
  Steelworks: "Steelworks: makes steel from iron ore and coal, brought to its station by rail from your mines. Join it to them with track.",
  "Munitions plant": "Munitions plant: makes rounds from steel, brought to its station by rail from a steelworks.",
  foundry: "Foundry: makes titans, and only titans (a barracks can't). Set it to a titan design; each takes up to an hour.",
  training_camp: "Training camp: designs with training are made only at a barracks within 12 hexes of one of these.",
  comms_post: "Comms post: 20 more command points (a formation takes one per 50 units). Over capacity, your newest formations hold and take no orders.",
  trade_post: "Trade post: a station on your track that sells goods to the AI market for credits, and takes delivery of what you buy there. Select it to choose what it sells.",
};

/** Free builds left, by structure ("mine", "station", "barracks") or plant
 *  recipe (the state's `construction.free`): the first few are free. */
const [free, setFreeBuilds] = createSignal<Record<string, number>>({});
export function setFree(state: Record<string, unknown>): void {
  setFreeBuilds((state.construction as { free?: Record<string, number> } | undefined)?.free ?? {});
  const list = state.recipes as Recipe[] | undefined;
  if (Array.isArray(list) && list.length) {
    try {
      localStorage.setItem("ferro.recipes", JSON.stringify(list));
    } catch {
      /* How to Play keeps its own list */
    }
  }
  if (Array.isArray(list) && list.length) setRecipes(list);
  const known = (state.research as { known?: number[] } | undefined)?.known ?? [];
  if (known.join() !== knownTechs().join()) setKnownTechs(known);
}

/** What a structure costs now, in words: "Free", "Free ×2", or its steel
 *  ("300 steel"); track is free until steel production is running. */
/** Free builds left of a kind ("mine", "track"). */
export const freeLeft = (kind: string) => free()[kind] ?? 0;

export function costOf(p: Placing): string {
  if (p.what === "track") return (free().track ?? 1) > 0 ? "Free" : "5 steel/hex";
  const n = free()[p.what === "plant" ? (p.recipe ?? "") : p.what] ?? 0;
  return n > 1 ? `Free ×${n}` : n === 1 ? "Free" : `${steelOf(p)} steel`;
}

/** What engineers can build, most wanted first (an engineer's build card
 *  follows this order, its first slots on keys). */
export const STRUCTURES: [string, Placing][] = [
  ["Mine", { what: "mine" }],
  ["Station", { what: "station" }],
  ["Track", { what: "track" }],
  ["Steelworks", { what: "plant", recipe: "Steelworks" }],
  ["Munitions plant", { what: "plant", recipe: "Munitions plant" }],
  ["Depot", { what: "depot" }],
  ["Barracks", { what: "barracks" }],
  ["Recruiting office", { what: "plant", recipe: "Recruiting office" }],
  ["Locomotive works", { what: "plant", recipe: "Locomotive works" }],
  ["Copper smelter", { what: "plant", recipe: "Copper smelter" }],
  ["Refinery", { what: "plant", recipe: "Refinery" }],
  ["Mint", { what: "plant", recipe: "Mint" }],
  ["Guard post", { what: "guard_post" }],
  ["Foundry", { what: "foundry" }],
  ["Storm breaker", { what: "breaker" }],
];

export function BuildTab(props: { place: (p: Placing) => void; planMines?: () => void }): JSX.Element {
  // The rows' bars fill between reports, as the map's do: redrawn a second.
  const timer = window.setInterval(() => setNow((n) => n + 1), 1000);
  onCleanup(() => clearInterval(timer));
  const is = (p: Placing) => {
    const now = placing();
    return !!now && now.what === p.what && now.resource === p.resource && now.recipe === p.recipe;
  };
  const [picking, setPicking] = createSignal(false);
  let chip!: HTMLButtonElement;
  const button = (label: string, p: Placing) => (
    <button class="chip" aria-pressed={is(p)} aria-disabled={lockedWhy(p) ? "true" : undefined} data-tip={lockedWhy(p) ?? STRUCTURE_TIPS[p.recipe ?? p.what] ?? (p.recipe ? recipeTip(p.recipe) : undefined)} onClick={() => !lockedWhy(p) && props.place(p)}>
      {label}
      <span class="chip-cost">{costOf(p)}</span>
    </button>
  );
  return (
    <div class="build-tab">
      <div class="view-row">
        <span class="row-label">Build</span>
        <div class="chips">
          {button("Depot", { what: "depot" })}
          {button("Station", { what: "station" })}
          {button("Trade post", { what: "trade_post" })}
          {button("Track", { what: "track" })}
          {button("Barracks", { what: "barracks" })}
          {button("Guard post", { what: "guard_post" })}
          {button("Foundry", { what: "foundry" })}
          <Show when={v31()}>
            {button("Training camp", { what: "training_camp" })}
            {button("Comms post", { what: "comms_post" })}
          </Show>
        </div>
      </div>
      <div class="view-row">
        <span class="row-label">Mine</span>
        <div class="chips">
          {/* One mine digs every ore in its reach. */}
          {button("Mine", { what: "mine" })}
          {/* Many at once: a box, and the server plans the best spots. */}
          <button
            class="chip"
            data-tip="Plan mines: drag a box over prospected ground; the best spots for mines in it (and, if you like, the track joining them) are laid out for you to build all at once."
            onClick={() => props.planMines?.()}
          >
            Plan mines…
          </button>
        </div>
      </div>
      <div class="view-row">
        <span class="row-label">Plant</span>
        <div class="chips">
          {button("Plant", { what: "plant", recipe: plantChoice() })}
          <span class="recipe-anchor">
            <button
              ref={chip}
              class="chip recipe-chip"
              data-recipe-opener
              aria-expanded={picking()}
              data-tip={recipeTip(plantChoice()) ?? plantChoice()}
              onClick={() => setPicking(!picking())}
            >
              <RecipeIcon recipe={plantChoice()} size={20} />
              {plantChoice()}
            </button>
            <Show when={picking()}>
              <RecipePicker
                anchor={chip}
                cost={(name) => costOf({ what: "plant", recipe: name })}
                current={[plantChoice()]}
                close={() => setPicking(false)}
                pick={(name) => {
                  setPlantChoice(name);
                  try {
                    localStorage.setItem("ferro.plantChoice", name);
                  } catch {
                    /* this session only */
                  }
                  setPicking(false);
                  // Picked: placing one, as the Plant button.
                  const p: Placing = { what: "plant", recipe: name };
                  if (!lockedWhy(p)) props.place(p);
                }}
              />
            </Show>
          </span>
        </div>
      </div>
      <Show when={stormWorld()}>
        <div class="view-row">
          <span class="row-label">Storm</span>
          <div class="chips">
            {/* Breaks the wall open 24 hexes round; spent when it's done. */}
            {button("Storm breaker", { what: "breaker" })}
          </div>
        </div>
      </Show>
      <Show when={sites().length}>
        <div class="sites">
          <span class="row-label">
            Building{" "}
            <span class="muted">
              {sites().filter((s) => !s.queued).length} / {siteLimit()} sites
              {(() => {
                const n = sites().filter((s) => s.queued).length;
                return n ? ` · ${n} queued` : "";
              })()}
            </span>
          </span>
          {/* A kind at a time (a long line of track is one row, not forty):
              how many, who's on them, how far along together; click to see
              each. */}
          <For each={groups()}>
            {(g) => (
              <Show when={g.sites.length > 1} fallback={siteRow(g.sites[0])}>
                <div class="site-row site-group" classList={{ blocked: g.sites.some((s) => !!s.blocked) }}>
                  <button class="site-group-head ghost" aria-expanded={open().has(g.name)} onClick={() => toggle(g.name)}>
                    {open().has(g.name) ? "▾" : "▸"} {g.name} ×{g.sites.length}
                    <span class="site-crew">
                      {g.sites.some((s) => !s.queued) ? crewOf(g.sites.filter((s) => !s.queued)) : ""}
                      {(() => {
                        const n = g.sites.filter((s) => s.queued).length;
                        return n ? ` · ${n} queued` : "";
                      })()}
                    </span>
                  </button>
                  <span class="bar">
                    <span style={{ width: `${(now(), Math.min(100, (100 * g.sites.reduce((a, s) => a + siteDone(s), 0)) / Math.max(1, g.sites.reduce((a, s) => a + s.need, 0))))}%` }} />
                  </span>
                  <button class="site-cancel" data-tip={`Cancel all ${g.sites.length}: their steel goes back home`} onClick={() => cancelSites(g.sites.map((s) => s.at))}>
                    ×
                  </button>
                </div>
                <Show when={open().has(g.name)}>
                  <div class="site-group-items">
                    <For each={g.sites}>{(s) => siteRow(s)}</For>
                  </div>
                </Show>
              </Show>
            )}
          </For>
        </div>
      </Show>
    </div>
  );
}

// Groups opened in the Build tab's list (kept while it's closed).
const [open, setOpen] = createSignal<Set<string>>(new Set());
const toggle = (name: string) =>
  setOpen((o) => {
    const n = new Set(o);
    if (n.has(name)) n.delete(name);
    else n.add(name);
    return n;
  });

/** The sites by kind, in the order they were placed. */
const groups = () => {
  const by = new Map<string, Site[]>();
  for (const s of sites()) by.set(siteName(s), [...(by.get(siteName(s)) ?? []), s]);
  return [...by].map(([name, list]) => ({ name, sites: list }));
};

/** Who's on some sites: engineers building or going to them. */
function crewOf(list: Site[]): string {
  const ids = new Set(list.map((s) => s.id));
  const n = engineers().filter((e) => ids.has(siteRef(e)?.id ?? -1) && (e.doing === "building" || e.doing === "going")).length;
  return n ? ` · ${n} engineer${n === 1 ? "" : "s"}` : engineers().length ? " · nobody on them" : "";
}

/** One site's row: what, who's on it, why it's stuck, how far, cancel. */
function siteRow(s: Site): JSX.Element {
  return (
    <div class="site-row" classList={{ blocked: !!s.blocked }}>
      <span>
        {name(s)}
        {/* Who's on it: an unattended site stands out. */}
        <span class="site-crew">
          {(() => {
            if (s.queued) return s.what === "breaker" ? " · waits for the wall: opens when a breach reaches it" : " · queued: opens when another finishes";
            const n = engineers().filter((e) => siteRef(e)?.id === s.id && (e.doing === "building" || e.doing === "going")).length;
            return n ? ` · ${n} engineer${n === 1 ? "" : "s"}` : engineers().length ? " · nobody on it" : "";
          })()}
        </span>
        <Show when={s.blocked}>
          <span class="site-why">{BLOCKED[s.blocked!]?.[1]}</span>
        </Show>
      </span>
      <span class="bar">
        <span style={{ width: `${(now(), Math.min(100, (100 * siteDone(s)) / Math.max(1, s.need)))}%` }} />
      </span>
      <button class="site-cancel" data-tip="Cancel: all the steel paid into it goes back home" onClick={() => cancelSites([s.at])}>
        ×
      </button>
    </div>
  );
}
